import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, Activity } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, loaded }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={loaded ? { y: 0 } : { y: "112%" }}
      transition={{ duration: 0.85, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ loaded }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yCopy = useTransform(scrollYProgress, [0, 1], [0, 50]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-paper pt-[72px]"
    >
      <div className="bg-grid-fade absolute inset-0" />
      <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-brand/5 blur-[120px]" />

      <motion.div
        style={{ opacity }}
        className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:py-24"
      >
        <motion.div style={{ y: yCopy }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={loaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: EASE, delay: 0.05 }}
            className="mb-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500 sm:text-[11px]"
          >
            <span className="h-px w-10 bg-brand" />
            JISLL Technology Services Pvt Ltd
          </motion.p>

          <h1 className="font-heading text-5xl font-black leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-7xl xl:text-[5.25rem]">
            <MaskedLine delay={0.15} loaded={loaded}>
              Nothing Missed.
            </MaskedLine>
            <MaskedLine delay={0.28} loaded={loaded}>
              <span className="text-brand">Nothing Broken.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={loaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: EASE, delay: 0.5 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            We build and operate enterprise software that refuses to fail —
            engineering, QA and 24/7 observability under one roof, so your
            dashboards stay green and your customers never notice us working.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={loaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: EASE, delay: 0.62 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-request-consultation"
              onClick={() => scrollToSection("#contact")}
              className="group flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-hover hover:shadow-[0_12px_32px_rgba(0,82,255,0.4)]"
            >
              Request Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-explore-products"
              onClick={() => scrollToSection("#products")}
              className="group flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-ink transition-all duration-200 hover:border-ink hover:bg-ink hover:text-white"
            >
              Explore Products
              <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yVisual }} className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={loaded ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 0.9, ease: EASE, delay: 0.45 }}
            className="relative"
          >
            <div
              data-testid="hero-visual-frame"
              className="relative overflow-hidden rounded-3xl rounded-bl-[4rem] border border-slate-200/70 shadow-[0_32px_80px_-24px_rgba(15,23,42,0.25)]"
            >
              <img
                src="https://images.unsplash.com/photo-1765371513276-a74f1ecbcf7d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
                alt="JISLL engineering team at work in a modern office"
                className="aspect-[4/5] w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(0,82,255,0.18),transparent_55%)]" />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={loaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease: EASE, delay: 0.85 }}
              data-testid="hero-card-leadership"
              className="absolute -left-4 top-8 rounded-2xl border border-slate-200/80 bg-white/90 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.12)] backdrop-blur-md sm:-left-8"
            >
              <p className="font-heading text-3xl font-extrabold tracking-tight text-ink">
                14+ Years
              </p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">
                Enterprise architecture leadership
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={loaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease: EASE, delay: 1 }}
              data-testid="hero-card-products"
              className="absolute -bottom-6 right-4 flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/90 py-4 pl-4 pr-6 shadow-[0_16px_40px_rgba(15,23,42,0.12)] backdrop-blur-md sm:-right-4"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <Activity className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-heading text-base font-bold text-ink">
                  3 Products in Development
                </span>
                <span className="mt-0.5 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-emerald-600">
                  <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-emerald-500" />
                  OPD · Tracker · KappawiseVet
                </span>
              </span>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={loaded ? { opacity: 1 } : {}}
        transition={{ delay: 1.3, duration: 0.6 }}
        onClick={() => scrollToSection("#about")}
        data-testid="hero-scroll-indicator"
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 font-mono text-[9px] uppercase tracking-[0.24em] text-slate-400 transition-colors hover:text-ink lg:flex"
        aria-label="Scroll to about"
      >
        Scroll
        <span className="h-8 w-px overflow-hidden bg-slate-200">
          <motion.span
            className="block h-3 w-px bg-brand"
            animate={{ y: [0, 32] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.button>
    </section>
  );
}
